import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';

import { ActionData } from '@bonusly/common-action-input';
import { createBonusData } from '@bonusly/common-give-box';
import { useHttpClient } from '@bonusly/http-client';
import { track } from '@bonusly/who-am-i';

import { Award, Bonus, CachedBonuses } from '../../../../../../../types/bonus';
import { useToast } from '../../../../../hooks/useToast';
import { ResourcePath } from '../../../api/hooks/ResourcePath';
import { QueryKey } from '../../../api/hooks/types';
import { useInvalidateApprovalRequests } from '../../../cache/invalidators/approvalRequests';
import { useInvalidateAwards } from '../../../cache/invalidators/awards';
import { addBonusToFeed } from '../../../cache/mutators/bonus';
import { useAwardModalState } from '../state';

// TODO: This is TS workaround and should be fixed at the source.
// ...and is a hilarious example of why TS can be dumb.
const createAwardData = (config: any) => {
	return createBonusData(config);
};

interface UseGiveAwardConfig {
	award: Award;
	onError?: (error: unknown) => void;
	onSuccess: VoidFunction;
}

export const useGiveAward = ({ award, onError, onSuccess }: UseGiveAwardConfig) => {
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');
	const { createSuccessToast } = useToast();
	const { t } = useTranslation('recognition');
	const { hideGiver, isTemplatedAward } = useAwardModalState();

	const invalidateApprovalRequests = useInvalidateApprovalRequests();
	const invalidateAwards = useInvalidateAwards();

	// in order to simplify the bonus data, inject the hide_giver flag into the award
	award.hide_giver = hideGiver;

	const { mutate, isLoading: isSubmitting } = useMutation<AxiosResponse<{ result: Bonus }>, unknown, ActionData, unknown>(
		(action) => {
			const bonus = createAwardData({ action, award });
			const data = { bonus };
			return http.post<{ result: Bonus }>(ResourcePath.Bonuses, data, {
				params: { include_children: true },
			});
		},
		{
			onError: (err: unknown) => {
				onError?.(err);
			},
			onSuccess: ({ data: { result: bonus } }, vars) => {
				if (award?.approval_process) {
					invalidateApprovalRequests();
				} else {
					queryClient.setQueriesData<CachedBonuses>([QueryKey.BonusesInfinite], (cached) => addBonusToFeed(bonus, cached));
				}

				createSuccessToast(
					t('awards.feedback.success', {
						context: !!award.approval_process ? 'approval' : 'no_approval',
						defaultValue: 'awards.feedback.success',
					})
				);

				track('Award Give Complete', {
					award_name: award.reason,
					award_type: isTemplatedAward ? 'Templated' : 'Non Templated',
					budget_timeframe: award.budget_period,
					recipient_count: vars.recipients.length,
					amount: vars.amount,
					hashtag_included: vars.hasHashtag,
					hide_giver: award.hide_giver,
					gif_included: vars.attachment?.category === 'social',
					image_included: vars.attachment?.category === 'image',
					video_included: vars.attachment?.category === 'video',
					tag_included: vars.hasHashtag,
					result: 'Success',
					reason: vars.text,
				});

				invalidateAwards();
				onSuccess();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};
